import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowRight, ArrowUpRight } from "lucide-react";
import { Reveal, SectionHeading } from "../components/site/Reveal";

const APP_FLOW = ["Aplicación actual", "Diagnóstico", "Arquitectura", "APIs", "Automatización", "Aplicación inteligente"];

export const AppsConsulting = () => (
  <section className="relative py-28 md:py-36" data-testid="apps-consulting">
    <div className="mx-auto max-w-7xl px-6 grid gap-16 lg:grid-cols-2 lg:items-center">
      <div>
        <SectionHeading
          eyebrow="Consultoría de Aplicaciones"
          title={<>Tus aplicaciones pueden hacer <span className="text-gradient">mucho más.</span></>}
          copy="Analizamos aplicaciones existentes, procesos, arquitectura e integraciones para identificar oportunidades de modernización, automatización y evolución tecnológica."
          testId="apps-heading"
        />
        <Reveal delay={0.2}>
          <Link
            to="/consultoria-aplicaciones"
            className="mt-8 inline-flex items-center gap-2 text-sm font-semibold text-[var(--green)] hover:gap-3 transition-all"
            data-testid="apps-link"
          >
            Conocer la metodología <ArrowRight size={15} />
          </Link>
        </Reveal>
      </div>

      <div className="relative">
        <div className="absolute left-[22px] top-3 bottom-3 w-px bg-[var(--line)]" aria-hidden="true" />
        <motion.div
          className="absolute left-[22px] top-3 bottom-3 w-px origin-top bg-gradient-to-b from-[var(--green)] via-[var(--cyan)] to-[var(--blue)]"
          initial={{ scaleY: 0 }}
          whileInView={{ scaleY: 1 }}
          viewport={{ once: true, margin: "-120px" }}
          transition={{ duration: 2, ease: "easeInOut" }}
          aria-hidden="true"
        />
        <ul className="space-y-5">
          {APP_FLOW.map((step, i) => (
            <Reveal key={step} delay={i * 0.14}>
              <li className="relative flex items-center gap-5 pl-1">
                <span className={`relative z-10 flex h-11 w-11 shrink-0 items-center justify-center rounded-xl border mono text-xs ${i === APP_FLOW.length - 1 ? "border-[var(--green)] bg-[var(--green)]/10 text-[var(--green)]" : "border-[var(--line)] bg-[var(--surface)] text-[var(--muted)]"}`}>
                  0{i + 1}
                </span>
                <span className={`rounded-xl border px-4 py-2.5 text-sm font-medium ${i === APP_FLOW.length - 1 ? "border-[var(--green)]/50 bg-[var(--green)]/5 text-[var(--ink)]" : "border-[var(--line)] bg-[var(--surface)]/70 text-[var(--ink)]/85"}`}>
                  {step}
                </span>
              </li>
            </Reveal>
          ))}
        </ul>
      </div>
    </div>
  </section>
);

const PHASES = [
  { tag: "LEGACY", title: "Monolito", text: "Una sola estructura rígida. Cambios lentos, integraciones difíciles.", tone: "muted" },
  { tag: "TRANSFORM", title: "APIs + integración + automatización", text: "Abrir, conectar y automatizar sin detener la operación.", tone: "cyan" },
  { tag: "FUTURE", title: "Aplicación moderna + IA + datos", text: "Escalable, inteligente y lista para lo que sigue.", tone: "green" },
];

export const Modernization = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start 80%", "end 60%"] });
  const lineScale = useTransform(scrollYProgress, [0, 1], [0, 1]);

  return (
    <section ref={ref} className="relative py-28 md:py-36 bg-[var(--bg2)]" data-testid="modernization">
      <div className="mx-auto max-w-7xl px-6">
        <SectionHeading
          eyebrow="Modernización"
          title={<>De sistema heredado a <span className="text-gradient">plataforma inteligente.</span></>}
          testId="modernization-heading"
        />
        <div className="relative mt-16">
          <div className="absolute left-0 right-0 top-6 hidden h-px bg-[var(--line)] lg:block" aria-hidden="true" />
          <motion.div
            className="absolute left-0 top-6 hidden h-px w-full origin-left bg-gradient-to-r from-[var(--green)] via-[var(--cyan)] to-[var(--blue)] lg:block"
            style={{ scaleX: lineScale }}
            aria-hidden="true"
          />
          <div className="grid gap-6 lg:grid-cols-3">
            {PHASES.map((p, i) => (
              <Reveal key={p.tag} delay={i * 0.2}>
                <div className="relative rounded-2xl border border-[var(--line)] bg-[var(--surface)]/70 p-7" data-testid={`phase-${p.tag.toLowerCase()}`}>
                  <span className={`mono text-[11px] tracking-[0.3em] ${p.tone === "green" ? "text-[var(--green)]" : p.tone === "cyan" ? "text-[var(--cyan)]" : "text-[var(--muted)]"}`}>
                    {p.tag}
                  </span>
                  <h3 className="mt-4 text-xl font-bold tracking-tight">{p.title}</h3>
                  <p className="mt-3 text-sm leading-relaxed text-[var(--muted)]">{p.text}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
        <Reveal delay={0.3}>
          <Link to="/transformacion-digital" className="mt-10 inline-flex items-center gap-2 text-sm font-semibold text-[var(--cyan)] hover:gap-3 transition-all" data-testid="modernization-link">
            Descubrir posibilidades <ArrowUpRight size={15} />
          </Link>
        </Reveal>
      </div>
    </section>
  );
};
